import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { resolveImage } from "@/lib/siteDefaults";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Single image upload for site content.
 * Props:
 *   value: string url
 *   onChange: (url: string) => void
 *   testId?: string
 *   aspectRatio?: string (tailwind aspect class)
 */
export function SingleImageUpload({ value, onChange, testId, aspectRatio = "aspect-[4/5]" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div className={`${aspectRatio} rounded-xl overflow-hidden bg-[#F4F1EA] border border-[#E5E2DC] relative group max-w-xs`}>
        {value ? (
          <>
            <img src={resolveImage(value)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-sm"
              aria-label="Remove image"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X strokeWidth={1.5} className="w-4 h-4" />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-stone-400 gap-2">
            <ImageIcon strokeWidth={1.25} className="w-8 h-8" />
            <span className="text-xs font-sans">No image</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid={testId ? `${testId}-upload-button` : undefined}
          className="inline-flex items-center gap-2 border border-[#E5E2DC] hover:border-[#1A1C1A] px-4 py-2 rounded-full text-xs font-sans tracking-wide transition-colors disabled:opacity-60"
        >
          {uploading ? <Loader2 strokeWidth={1.5} className="w-4 h-4 animate-spin" /> : <Upload strokeWidth={1.5} className="w-4 h-4" />}
          {uploading ? "Uploading…" : value ? "Replace" : "Upload image"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={testId ? `${testId}-file-input` : undefined}
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
      <input
        type="text"
        placeholder="Or paste an image URL…"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-stone-300 py-2 text-sm font-sans focus:border-[#1A1C1A] focus:outline-none"
        data-testid={testId ? `${testId}-url-input` : undefined}
      />
    </div>
  );
}

/**
 * Multi image upload for product images.
 * Props:
 *   value: string[]
 *   onChange: (urls: string[]) => void
 */
export function MultiImageUpload({ value = [], onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const f of arr) {
      if (!f.type.startsWith("image/")) continue;
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      } catch (err) {
        toast.error(`Failed: ${f.name}`);
      }
    }
    if (uploaded.length) {
      onChange([...(value || []), ...uploaded]);
      toast.success(`Uploaded ${uploaded.length}`);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
        {value.map((url, i) => (
          <div key={i} className="aspect-[4/5] rounded-lg overflow-hidden bg-[#F4F1EA] relative group">
            <img src={resolveImage(url)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X strokeWidth={1.5} className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid={testId ? `${testId}-add-button` : undefined}
          className="aspect-[4/5] rounded-lg border-2 border-dashed border-stone-300 hover:border-[#1A1C1A] flex flex-col items-center justify-center gap-2 text-stone-500 hover:text-[#1A1C1A] transition-colors"
        >
          {uploading ? (
            <Loader2 strokeWidth={1.5} className="w-6 h-6 animate-spin" />
          ) : (
            <>
              <Upload strokeWidth={1.5} className="w-6 h-6" />
              <span className="text-xs font-sans">Add photo{value.length ? "s" : ""}</span>
            </>
          )}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => upload(e.target.files)}
      />
    </div>
  );
}
